import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  DashboardToday,
  TodayAppointmentRow,
  TodayCounts,
  TodayOwnerBlock,
  sendAppointmentConfirmSms,
} from "../../../api/dashboardService";
import { CARD, LINK_BTN, SectionTitle, TD, TD_NUM, TH, TH_NUM, TR } from "../../analytics/components/ui";
import { formatNumber } from "../../analytics/analyticsUtils";
import { notifySmsBalanceChanged } from "../../../utils/sms/smsToast";
import {
  STRIP_TILES,
  StripTile,
  accountBanners,
  formatClock,
  overdueLabel,
  sourceLabel,
  stripFootnote,
  waitLabel,
} from "../dashboardTodayUtils";

const patientPath = (id: string | null) => (id ? `/client/patients/edit-patients/${id}` : null);
/** The Appointments list. It does not read a date from the URL, so links cannot preselect a day. */
const APPOINTMENTS_PATH = "/client/appointments/view-appointment";

const TILE_TONE: Record<StripTile["tone"], string> = {
  neutral: "text-slate-800 dark:text-slate-100",
  info: "text-primary dark:text-primaryLight",
  warn: "text-amber-600 dark:text-amber-400",
  good: "text-emerald-600 dark:text-emerald-400",
  bad: "text-rose-600 dark:text-rose-400",
};

function Card({ title, hint, action, children, accent }: { title: string; hint?: string; action?: React.ReactNode; children: React.ReactNode; accent?: boolean }) {
  return (
    <section className={`${CARD} p-4 ${accent ? "ring-2 ring-primary/30 dark:ring-primaryLight/30" : ""}`}>
      <SectionTitle hint={hint} action={action}>
        {title}
      </SectionTitle>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">{children}</p>;
}

function SourceChip({ source }: { source: string }) {
  if (source === "appointment") return null;
  return (
    <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300">
      {sourceLabel(source)}
    </span>
  );
}

function PatientCell({ row }: { row: Pick<TodayAppointmentRow, "patientId" | "patientName" | "source"> }) {
  const navigate = useNavigate();
  const to = patientPath(row.patientId);
  const name = row.patientName || "Unnamed patient";
  return (
    <span className="inline-flex items-center">
      {to ? (
        <button type="button" className="font-medium text-slate-800 hover:text-primary hover:underline dark:text-slate-100" onClick={() => navigate(to)}>
          {name}
        </button>
      ) : (
        <span className="font-medium text-slate-800 dark:text-slate-100">{name}</span>
      )}
      <SourceChip source={row.source} />
    </span>
  );
}

/** Today strip: six counts, same buckets as the Appointments page status cards. */
export function TodayStrip({ counts }: { counts: TodayCounts }) {
  const navigate = useNavigate();
  const foot = stripFootnote(counts);
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {STRIP_TILES.map((tile) => (
          <button
            key={tile.key}
            type="button"
            title={tile.hint}
            onClick={() => navigate(APPOINTMENTS_PATH)}
            className={`${CARD} px-4 py-3 text-left hover:shadow-xl`}
          >
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{tile.label}</p>
            <p className={`mt-1 text-2xl font-bold tabular-nums ${TILE_TONE[tile.tone]}`}>{formatNumber(counts[tile.key])}</p>
          </button>
        ))}
      </div>
      {foot && <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{foot}</p>}
    </div>
  );
}

/** Today's remaining appointments in time order. */
export function NextUp({ rows }: { rows: TodayAppointmentRow[] }) {
  const navigate = useNavigate();
  return (
    <Card
      accent
      title="Next up"
      hint="Still to come and waiting, in time order"
      action={
        <button type="button" className={LINK_BTN} onClick={() => navigate(APPOINTMENTS_PATH)}>
          All of today
        </button>
      }
    >
      {rows.length === 0 ? (
        <Empty>Nobody else is booked for today.</Empty>
      ) : (
        <div className="overflow-x-auto -mx-4">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/80 dark:bg-slate-700/50">
                <th className={TH}>Time</th>
                <th className={TH}>Patient</th>
                <th className={TH}>Dentist</th>
                <th className={TH}>Room</th>
                <th className={TH}>Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {rows.map((r) => (
                <tr key={r.appointmentId} className={TR}>
                  <td className={`${TD} tabular-nums font-semibold`}>{formatClock(r.startTime)}</td>
                  <td className={TD}>
                    <PatientCell row={r} />
                  </td>
                  <td className={TD}>{r.doctorName}</td>
                  <td className={TD}>{r.room || "—"}</td>
                  <td className={TD}>
                    {r.status === "waiting" || r.status === "in_queue" || r.status === "inqueue" ? (
                      <span className="text-amber-600 dark:text-amber-400 font-medium">Waiting</span>
                    ) : (
                      <span className="text-slate-500 dark:text-slate-400">Not arrived</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** Waiting room, longest wait first. */
export function WaitingRoom({ rows, longWaitMinutes }: { rows: DashboardToday["waiting"]; longWaitMinutes: number }) {
  return (
    <Card title="Waiting room" hint={`Longest wait first · flagged after ${longWaitMinutes} min`}>
      {rows.length === 0 ? (
        <Empty>Nobody is waiting.</Empty>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-700">
          {rows.map((r) => (
            <li key={r.appointmentId} className="py-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <PatientCell row={r} />
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {r.doctorName}
                  {r.startTime ? ` · booked ${formatClock(r.startTime)}` : ""}
                </p>
              </div>
              <span
                className={`shrink-0 px-2 py-1 rounded-lg text-xs font-semibold tabular-nums ${
                  r.longWait
                    ? "bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400"
                    : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                }`}
              >
                {waitLabel(r.waitedMinutes)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** One row per dentist; the logged-in dentist first. */
export function PerDentist({ rows }: { rows: DashboardToday["dentists"] }) {
  if (rows.length === 0) return null;
  return (
    <Card title="Per dentist" hint="Today, cancelled left out">
      <div className="overflow-x-auto -mx-4">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50/80 dark:bg-slate-700/50">
              <th className={TH}>Dentist</th>
              <th className={TH_NUM}>Booked</th>
              <th className={TH_NUM}>Done</th>
              <th className={TH_NUM}>Waiting</th>
              <th className={TH_NUM}>To come</th>
              <th className={TH}>Next patient</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {rows.map((r) => (
              <tr key={r.doctorId} className={`${TR} ${r.isMe ? "bg-primary/5 dark:bg-primaryLight/10" : ""}`}>
                <td className={`${TD} font-medium`}>
                  {r.name}
                  {r.isMe && <span className="ml-2 text-[10px] font-semibold uppercase text-primary dark:text-primaryLight">You</span>}
                </td>
                <td className={TD_NUM}>{r.booked}</td>
                <td className={TD_NUM}>{r.done}</td>
                <td className={TD_NUM}>{r.waiting}</td>
                <td className={TD_NUM}>{r.toCome}</td>
                <td className={TD}>{r.next ? `${formatClock(r.next.startTime)} · ${r.next.patientName}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Owner only: SMS credit and subscription banners. No money on the Dashboard. */
export function AccountAlerts({ owner }: { owner: TodayOwnerBlock }) {
  const navigate = useNavigate();
  const banners = accountBanners(owner.sms, owner.subscription, owner.channelAlerts);
  if (!banners.length) return null;
  return (
    <div className="space-y-3">
      {banners.map((b) => (
        <div
          key={b.key}
          role="status"
          className={`flex flex-wrap items-center justify-between gap-2 rounded-xl px-4 py-3 text-sm border ${
            b.tone === "bad"
              ? "bg-rose-50 border-rose-200 text-rose-700 dark:bg-rose-900/20 dark:border-rose-800/50 dark:text-rose-300"
              : "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800/50 dark:text-amber-300"
          }`}
        >
          <span className="inline-flex items-center gap-2">
            <WarningAmberIcon sx={{ fontSize: 18 }} />
            {b.text}
          </span>
          {b.action && (
            <button type="button" className="font-semibold underline" onClick={() => navigate(b.action!.to)}>
              {b.action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/** Tomorrow's bookings with a one-click confirmation SMS. */
export function TomorrowList({ list }: { list: DashboardToday["tomorrowList"] }) {
  const [sending, setSending] = useState<string | null>(null);
  const [sent, setSent] = useState<Set<string>>(() => new Set());

  const send = (id: string) => {
    if (sending) return;
    setSending(id);
    sendAppointmentConfirmSms(id)
      .then((res) => {
        if (res?.sms?.sent || res?.success) {
          toast.success(res?.message || "Confirmation SMS sent");
          notifySmsBalanceChanged();
          setSent((prev) => new Set(prev).add(id));
        } else {
          toast.error(res?.message || res?.error || "Failed to send confirmation SMS");
        }
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Failed to send confirmation SMS"))
      .finally(() => setSending(null));
  };

  if (list.count === 0) return null;
  return (
    <Card
      title={`Tomorrow · ${list.count} booked`}
      hint="Send a confirmation SMS the day before"
    >
      <ul className="divide-y divide-slate-100 dark:divide-slate-700 max-h-[320px] overflow-y-auto">
        {list.items.map((r) => (
          <li key={r.appointmentId} className="py-2 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <span className="tabular-nums font-semibold mr-2 text-slate-700 dark:text-slate-200">{formatClock(r.startTime)}</span>
              <PatientCell row={r} />
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{r.doctorName}</p>
            </div>
            {sent.has(r.appointmentId) ? (
              <span className="shrink-0 text-xs font-medium text-emerald-600 dark:text-emerald-400">SMS sent</span>
            ) : (
              <button
                type="button"
                disabled={!!sending || !r.phone}
                title={r.phone ? "Send confirmation SMS" : "No phone number on file"}
                onClick={() => send(r.appointmentId)}
                className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold border border-primary text-primary hover:bg-primary hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-primary transition-colors dark:border-primaryLight dark:text-primaryLight"
              >
                {sending === r.appointmentId ? "Sending…" : "Send SMS"}
              </button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function RowList<T>({ rows, render, keyOf }: { rows: T[]; render: (row: T) => React.ReactNode; keyOf: (row: T) => string }) {
  return (
    <ul className="divide-y divide-slate-100 dark:divide-slate-700">
      {rows.map((r) => (
        <li key={keyOf(r)} className="py-2 flex items-center justify-between gap-3 text-sm">
          {render(r)}
        </li>
      ))}
    </ul>
  );
}

export function TreatedNotBilled({ rows }: { rows: TodayAppointmentRow[] }) {
  return (
    <Card title="Treated, not billed" hint="Completed today with no payment taken yet">
      <RowList
        rows={rows}
        keyOf={(r) => r.appointmentId}
        render={(r) => (
          <>
            <div className="min-w-0">
              <PatientCell row={r} />
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                {r.doctorName} · {formatClock(r.startTime)}
              </p>
            </div>
            <span className="shrink-0 text-xs text-amber-600 dark:text-amber-400 font-medium">No payment</span>
          </>
        )}
      />
    </Card>
  );
}

export function OnlineToResolve({ data }: { data: DashboardToday["onlineToResolve"] }) {
  const navigate = useNavigate();
  return (
    <Card
      title="Online bookings to resolve"
      hint="BookMyDentist patients to match, and pay-at-clinic bookings today"
      action={
        data.provisionalCount > 0 ? (
          <button type="button" className={LINK_BTN} onClick={() => navigate("/client/patients/patients-list/active")}>
            Resolve
          </button>
        ) : undefined
      }
    >
      {data.provisionalCount > 0 && (
        <>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {data.provisionalCount} provisional patient{data.provisionalCount === 1 ? "" : "s"}
          </p>
          <RowList
            rows={data.provisional}
            keyOf={(p) => p.patientId}
            render={(p) => (
              <>
                <span className="font-medium text-slate-800 dark:text-slate-100 truncate">{p.patientName || "Unnamed"}</span>
                <span className="shrink-0 text-xs text-slate-500 dark:text-slate-400 tabular-nums">{p.regNo}</span>
              </>
            )}
          />
        </>
      )}
      {data.unpaidOnlineToday.length > 0 && (
        <>
          <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Pay at clinic today</p>
          <RowList
            rows={data.unpaidOnlineToday}
            keyOf={(r) => r.appointmentId}
            render={(r) => (
              <>
                <PatientCell row={r} />
                <span className="shrink-0 text-xs tabular-nums text-slate-500 dark:text-slate-400">{formatClock(r.startTime)}</span>
              </>
            )}
          />
        </>
      )}
    </Card>
  );
}

export function UnfinishedPlans({ rows }: { rows: DashboardToday["unfinishedPlans"] }) {
  const navigate = useNavigate();
  return (
    <Card title="Unfinished treatment plans" hint="Planned work and no upcoming appointment: worth a call">
      <RowList
        rows={rows}
        keyOf={(r) => r.patientId}
        render={(r) => (
          <>
            <div className="min-w-0">
              <button
                type="button"
                className="font-medium text-slate-800 hover:text-primary hover:underline dark:text-slate-100"
                onClick={() => navigate(patientPath(r.patientId)!)}
              >
                {r.patientName || "Unnamed patient"}
              </button>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                {r.openLines} open item{r.openLines === 1 ? "" : "s"}
                {r.phone ? ` · ${r.phone}` : ""}
              </p>
            </div>
          </>
        )}
      />
    </Card>
  );
}

export function LabDue({ rows }: { rows: DashboardToday["labDue"] }) {
  const navigate = useNavigate();
  return (
    <Card
      title="Lab work due"
      hint="Open lab orders due today or earlier"
      action={
        <button type="button" className={LINK_BTN} onClick={() => navigate("/client/labs/orders")}>
          Lab orders
        </button>
      }
    >
      <RowList
        rows={rows}
        keyOf={(r) => r.labOrderId}
        render={(r) => (
          <>
            <div className="min-w-0">
              <span className="font-medium text-slate-800 dark:text-slate-100 truncate">{r.patient || "Lab order"}</span>
              <p className="text-xs text-slate-500 dark:text-slate-400">{r.orderStatus}</p>
            </div>
            <span className={`shrink-0 text-xs font-medium ${r.overdueDays > 0 ? "text-rose-600 dark:text-rose-400" : "text-amber-600 dark:text-amber-400"}`}>
              {overdueLabel(r.overdueDays)}
            </span>
          </>
        )}
      />
    </Card>
  );
}

export function LowStock({ data }: { data: DashboardToday["lowStock"] }) {
  const navigate = useNavigate();
  return (
    <Card
      title={`Low stock · ${data.count}`}
      hint="At or below the alert quantity"
      action={
        <button type="button" className={LINK_BTN} onClick={() => navigate("/client/inventory/product")}>
          Products
        </button>
      }
    >
      <RowList
        rows={data.items}
        keyOf={(p) => p.productId}
        render={(p) => (
          <>
            <span className="font-medium text-slate-800 dark:text-slate-100 truncate">{p.productName}</span>
            <span className="shrink-0 text-xs tabular-nums text-rose-600 dark:text-rose-400">
              {formatNumber(p.availableQuantity, 2)} {p.scale} left · alert at {formatNumber(p.alertQuantity, 2)}
            </span>
          </>
        )}
      />
    </Card>
  );
}
